import {
  useMutation,
  useQueries,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { useProductsQuery } from '../products/api';
import type { InventoryAdjustFormValues } from '../products/schemas';
import type { Inventory, ProductVariantWithStock, ProductWithStock } from '../products/types';

import { ApiError, apiRequest } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * No endpoint exists to list inventory across a shop, and none exists for
 * movement history (`InventoryController` has exactly two adjust routes —
 * see below). This assembles a real, non-fabricated view from the GET
 * endpoints that DO carry derived stock: `GET /merchant/products` (a
 * SIMPLE product's own stock, `product_id`-keyed) and
 * `GET /merchant/products/:id/variants` (a variant product's stock,
 * `product_variant_id`-keyed) — one request per product's variants, not a
 * single call, because that single call does not exist.
 *
 * Sprint UX/UI Merchant Dashboard V2 — real gap found and closed: this
 * hook used to query ONLY variants, so a shop selling exclusively simple
 * products (no variants at all) got an empty inventory overview —
 * `rows: []` — even though those products carry real, read-derived stock
 * on `GET /merchant/products` the whole time. `variantId: null` marks a
 * simple-product row; `useAdjustInventoryMutation` below already knew
 * how to route by kind (the API has offered
 * `PUT .../inventory/product/{productId}/adjust`, Sprint 11, since
 * before this fix — simply never consumed by this screen).
 */
export interface InventoryRow {
  readonly productId: string;
  readonly productName: string;
  readonly variantId: string | null;
  readonly sku: string;
  readonly stock: ProductVariantWithStock['stock'];
}

export interface InventoryOverview {
  readonly rows: readonly InventoryRow[];
  readonly isPending: boolean;
  readonly isError: boolean;
  /** The API answered 403 (the member's role has no access to the catalogue): not a failure to report as one. */
  readonly isForbidden: boolean;
}

export function useInventoryOverviewQuery(): InventoryOverview {
  const { accessToken, activeShopId } = useSession();
  const productsQuery = useProductsQuery();
  const products: readonly ProductWithStock[] = productsQuery.data ?? [];

  const variantQueries = useQueries({
    queries: products.map((product) => ({
      queryKey: ['products', activeShopId, product.id, 'variants'],
      queryFn: () =>
        apiRequest<readonly ProductVariantWithStock[]>(
          `/merchant/products/${product.id}/variants`,
          { accessToken, shopId: activeShopId },
        ),
      enabled: accessToken !== null && activeShopId !== null,
    })),
  });

  const rows = useMemo<readonly InventoryRow[]>(() => {
    const result: InventoryRow[] = [];
    products.forEach((product, index) => {
      const variants = variantQueries[index]?.data ?? [];
      if (variants.length === 0) {
        // A simple product — its OWN stock (product_id-keyed), never
        // surfaced by this hook before this fix.
        result.push({
          productId: product.id,
          productName: product.name,
          variantId: null,
          sku: product.sku,
          stock: product.stock,
        });
        return;
      }
      for (const variant of variants) {
        result.push({
          productId: product.id,
          productName: product.name,
          variantId: variant.id,
          sku: variant.sku,
          stock: variant.stock,
        });
      }
    });
    return result;
  }, [products, variantQueries]);

  return {
    rows,
    isPending: productsQuery.isPending || variantQueries.some((q) => q.isPending),
    isError: productsQuery.isError || variantQueries.some((q) => q.isError),
    isForbidden: [productsQuery, ...variantQueries].some(
      (q) => q.error instanceof ApiError && q.error.status === 403,
    ),
  };
}

/**
 * Sprint UX/UI Merchant Dashboard V2 — now routes to whichever of the
 * two REAL adjust endpoints matches the row: `variantId !== null` hits
 * `PUT .../inventory/{variantId}/adjust` (Sprint 8, Lot 2); `variantId
 * === null` hits `PUT .../inventory/product/{productId}/adjust`
 * (Sprint 11) — both already existed; this is the first UI consumer of
 * the second one.
 */
export function useAdjustInventoryMutation(
  productId: string,
  variantId: string | null,
): UseMutationResult<Inventory, ApiError, InventoryAdjustFormValues> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: InventoryAdjustFormValues) =>
      apiRequest<Inventory>(
        variantId !== null
          ? `/merchant/inventory/${variantId}/adjust`
          : `/merchant/inventory/product/${productId}/adjust`,
        {
          method: 'PUT',
          body: {
            delta: values.delta,
            ...(values.reason !== undefined ? { reason: values.reason } : {}),
            ...(values.quantityMinThreshold !== undefined
              ? { quantityMinThreshold: values.quantityMinThreshold }
              : {}),
          },
          accessToken,
          shopId: activeShopId,
        },
      ),
    onSuccess: () => {
      if (variantId !== null) {
        void queryClient.invalidateQueries({
          queryKey: ['products', activeShopId, productId, 'variants'],
        });
      } else {
        void queryClient.invalidateQueries({ queryKey: ['products', activeShopId] });
      }
    },
  });
}
